'use client';
import { Button, Typography } from '@mui/material';
import { useId, useRef, useState } from 'react';
import { ConfirmActions } from '../modal';

const MIN_FILE_SIZE = 1024; // 1KB
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

/** Why a picked file can't be uploaded, or undefined when it can. */
export const fileUploadError = (file: File) => {
  if (!ALLOWED_TYPES.includes(file.type)) return 'Formato inválido. Elegí un PDF, JPG, JPEG o PNG.';
  if (file.size > MAX_FILE_SIZE) return 'El archivo pesa más de 15 MB.';
  if (file.size < MIN_FILE_SIZE) return 'El archivo es demasiado chico (1 KB mínimo).';
  return undefined;
};

/**
 * Body of the document upload dialog: pick a file, see right away if it can't be uploaded, then upload it. "Subir
 * archivo" stays disabled until a valid file is picked, and a failed upload keeps the dialog open with the file
 * still selected, so the user can retry.
 *
 * @param {Function} onUpload - Uploads the file; resolves true when done (the caller closes the dialog), false on
 * failure (the caller explains why, e.g. with a toast).
 * @param {Function} onCancel - Closes the dialog without uploading.
 */
export const FileUploadDialogBody = ({
  onUpload,
  onCancel,
}: {
  onUpload: (file: File) => Promise<boolean>;
  onCancel: () => void;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const errorId = useId();
  const error = file ? fileUploadError(file) : undefined;

  const upload = async () => {
    if (!file || error) return;
    setLoading(true);
    const done = await onUpload(file).catch(() => false);
    // On success the dialog closes and this body unmounts
    if (!done) setLoading(false);
  };

  return (
    <>
      <Typography id={hintId} variant="body1" color="textSecondary">
        Tiene que ser un PDF o una imagen (JPG, JPEG o PNG) de hasta 15 MB.
      </Typography>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
        aria-label="Elegir archivo"
        tabIndex={-1}
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        style={{ display: 'none' }}
      />
      {/* Real button (keyboard reachable) that opens the hidden native file picker */}
      <span>
        <Button
          variant="outlined"
          disabled={loading}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          onClick={() => fileInputRef.current?.click()}
        >
          {file ? 'Elegir otro archivo' : 'Elegir archivo'}
        </Button>
      </span>
      <Typography aria-live="polite" variant="body2" color="primary" sx={{ wordBreak: 'break-word' }}>
        {file ? `Archivo elegido: ${file.name}` : 'Todavía no elegiste un archivo'}
      </Typography>
      {error && (
        <Typography id={errorId} role="alert" variant="body2" color="error">
          {error}
        </Typography>
      )}
      <ConfirmActions
        confirmLabel="Subir archivo"
        loadingLabel="Subiendo…"
        loading={loading}
        disabled={!file || !!error}
        onConfirm={upload}
        onCancel={onCancel}
      />
    </>
  );
};
